import { NewTaskDotBackground } from './NewTaskDotBackground';
import { cloneFrameSources } from './clone-frame-sources';
import {failedSlotPlaceholders,type FailedSlotRun} from './canvas-failed-slots';
import '@xyflow/react/dist/style.css';
import { useEffect, useCallback, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useReactFlow,
  ReactFlowProvider,
  Panel,
  PanOnScrollMode,
  type Node,
  type OnNodeDrag,
  type OnNodesChange,
  type NodeMouseHandler,
} from '@xyflow/react';
import { MousePointer2, Hand, Undo2, Redo2 } from 'lucide-react';
import { useCanvasStore, getImageBatchId } from './canvas-store';
import { useQuery } from '@tanstack/react-query';
import {
  useActiveProjectImages,
  useActiveProjectMessages,
  useActiveProjectCanvasPlaceholders,
  useActiveProjectPendingFitToView,
  useActiveProjectPendingFitToRow,
  useActiveProjectPendingPanTarget,
  useActiveProjectLoading,
  useActiveProjectId,
} from './store-selectors';
import { ImageNode } from '@/components/canvas/ImageNode';
import { PlaceholderNode } from '@/components/canvas/PlaceholderNode';
// Static import is cycle-safe FROM HERE: the runner imports canvas-store
// (which imports no components), never this workspace. The store itself
// cannot statically import the runner (runner → store already), which is why
// the tile-index rebuild hooks in at this component's project-load effect
// instead of inside loadOrCreateProject/switchProject.
import { rebuildVideoCloneTileIndex } from './video-clone-runner';
import { CaptionStylePicker } from '@/components/canvas/CaptionStylePicker';
import type { CanvasImage } from './canvas-types';
import type { CanvasPlaceholder } from './canvas-types';
import { computeSnapAndGuides, type GuideLines } from './use-alignment-guides';
import { CanvasBrushMode } from './canvas-brush-mode';
import { CanvasCommentPin } from './canvas-comment-pin';

/**
 * v4.19 — Funnel-stage section frame. Rendered BEHIND a stage's row of ads
 * (Full Funnel / angles-to-campaign builds) as a titled border so TOF/MOF/
 * BOF/Retargeting read as visually separated sections. Derived client-side
 * from each tile's adAngleId → funnel stage (no persistence, works on
 * reload and on projects built before this shipped).
 */
function FunnelStageFrameNode({ data }: { data: { label: string; width: number; height: number } }) {
  return (
    <div
      style={{
        width: data.width,
        height: data.height,
        border: '1.5px dashed #C9C2B6',
        borderRadius: 16,
        background: 'rgba(123,31,64, 0.03)',
        pointerEvents: 'none',
        position: 'relative',
      }}
    >
      {/* Title chip sits INSIDE the frame's top band. It is ALSO the drag
          handle for the whole section — grab it to move the box + every ad
          inside (v4.23). pointerEvents re-enabled just for the chip. */}
      <div
        className="frame-drag-handle"
        title={`Drag to move the whole ${data.label} section`}
        style={{
          position: 'absolute',
          top: 10,
          left: 14,
          padding: '4px 14px',
          borderRadius: 999,
          background: '#7b1f40',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.4,
          whiteSpace: 'nowrap',
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
      >
        {data.label}
      </div>
    </div>
  );
}

const NODE_TYPES = {
  imageNode: ImageNode,
  placeholderNode: PlaceholderNode,
  funnelFrameNode: FunnelStageFrameNode,
};

const STAGE_FRAME_TITLES: Record<string, string> = {
  TOF: 'Top of Funnel',
  MOF: 'Middle of Funnel',
  BOF: 'Bottom of Funnel',
  RETARGETING: 'Retargeting',
  COMPETITOR: 'Competitor',
};
const FRAME_PAD = 24;
// Deeper top padding gives the title chip its own clear band above the ads
// (chip ≈26px tall at top:10) instead of overlapping the tile tops.
const FRAME_PAD_TOP = 52;

/** Friendly frame titles per playbook (non-funnel generations). */
const PLAYBOOK_FRAME_LABELS: Record<string, string> = {
  'campaign-v2': 'Campaign',
  'campaign-targeted': 'Campaign',
  'campaign-v3': 'Winning Ads',
  'competitor-ads': 'Competitor Ads',
  'variations-v3': 'Variations',
  'copy-variations': 'Copy Variations',
  'edit-fixes': 'Edits',
  'video-clone': 'Clone Video Ad',
  // v6.76 — Ad-library / picker batch clones (create-variations endpoint in
  // clone mode). The run row exists so the landed clones keep a titled box.
  'clone-batch': 'Clone',
  // v6.78 — angle builds get ONE "Angles" box (Dan killed the funnel
  // classification: TOF/MOF/BOF boxes now belong to Full Funnel runs only).
  'angles-to-campaign': 'Angles',
  'full_funnel_campaign': 'Campaign',
};

/**
 * One titled frame per generation batch: funnel-staged runs get a frame per
 * (workflowRunId, stage) labeled TOF/MOF/…; every other playbook run gets a
 * single frame labeled from its playbook ("Campaign", "Variations", …).
 */
/** Cluster glue — v4.42 axis-asymmetric: X wide enough to bridge a REMOVED
 * middle tile (tile 320 + 2×40 gaps = 400 edge-to-edge, so 440 covers it —
 * pulling one tile out must NOT split the box); Y tight (rows are 160-200px
 * apart — a tall Y glue would merge stacked section boxes). */
const CLUSTER_GAP_X = 440;
const CLUSTER_GAP_Y = 100;
/** v7.49 — Y glue INSIDE one run's own tiles. Clustering is scoped per seed
 * (run, or run+stage for Full Funnel), so this can never merge two different
 * runs or two funnel stages — only rows of the SAME generation. The grid
 * lays rows out on a 520px pitch with 320px tiles, i.e. a 200px vertical
 * gap, so the 100px glue above split every multi-row run into one box per
 * row: "make 5 ads" (one row) looked right while "make 20 ads" drew three
 * boxes for one request (Dan, Sep 4). */
const CLUSTER_GAP_Y_WITHIN_RUN = 260;

function rectsTouch(a: CanvasImage, b: CanvasImage, gapX: number, gapY: number): boolean {
  return (
    a.x - gapX < b.x + b.displayWidth &&
    a.x + a.displayWidth + gapX > b.x &&
    a.y - gapY < b.y + b.displayHeight &&
    a.y + a.displayHeight + gapY > b.y
  );
}

/**
 * v4.23 — GEOMETRIC membership. A section's box is the largest proximity
 * cluster of its run's tiles — drag a tile away and the box lets it go
 * (no more stretching to chase it). Conversely any tile parked inside a
 * box's area is adopted into it. data.memberIds powers title-chip group
 * drag (move the box = move every member).
 */
type PendingRect = { taskId?: string; x: number; y: number; w: number; h: number; batchId?: string; label?: string; workflowRunId?: number };

function buildStageFrameNodes(
  images: CanvasImage[],
  stageByAngleId: Map<number, string>,
  runLabelById: Map<number, string>,
  pending: PendingRect[] = [],
  fullFunnelRunIds: Set<number> = new Set(),
  cloneSources: Map<number, CanvasImage> = new Map(),
): Node[] {
  const seeds = new Map<string, { label: string; members: CanvasImage[] }>();
  for (const img of images) {
    if (img.workflowRunId == null) continue;
    // v6.78 — TOF/MOF/BOF stage boxes ONLY for Full Funnel runs (the one
    // playbook whose identity IS the funnel). Every other angle build gets a
    // single run-labeled "Angles" box; funnelStage is metadata, not layout.
    const stage = img.adAngleId != null && fullFunnelRunIds.has(img.workflowRunId)
      ? stageByAngleId.get(img.adAngleId)
      : undefined;
    let key: string;
    let label: string;
    if (stage && STAGE_FRAME_TITLES[stage]) {
      key = `${img.workflowRunId}:${stage}`;
      label = STAGE_FRAME_TITLES[stage];
    } else {
      const runLabel = runLabelById.get(img.workflowRunId);
      if (!runLabel) continue; // runs list not loaded / unknown playbook — no frame
      key = `run:${img.workflowRunId}`;
      label = runLabel;
    }
    const s = seeds.get(key) ?? { label, members: [] };
    s.members.push(img);
    seeds.set(key, s);
  }

  for (const [runId,source] of Array.from(cloneSources)) {
    const key=`run:${runId}`;
    const seed=seeds.get(key) ?? {label:runLabelById.get(runId) ?? 'Clone',members:[]};
    if(!seed.members.some(image=>image.id===source.id))seed.members.push(source);
    seeds.set(key,seed);
  }

  // Core = largest proximity cluster of each seed group (outliers = dragged out).
  const cores: Array<{ key: string; label: string; core: CanvasImage[] }> = [];
  const coreAssigned = new Set<string>();
  seeds.forEach((s, key) => {
    const unvisited = new Set<CanvasImage>(s.members);
    let best: CanvasImage[] = [];
    while (unvisited.size > 0) {
      const start = Array.from(unvisited)[0];
      unvisited.delete(start);
      const cluster = [start];
      const queue = [start];
      while (queue.length > 0) {
        const cur = queue.pop()!;
        Array.from(unvisited).forEach((other) => {
          if (rectsTouch(cur, other, CLUSTER_GAP_X, CLUSTER_GAP_Y_WITHIN_RUN)) {
            unvisited.delete(other);
            cluster.push(other);
            queue.push(other);
          }
        });
      }
      if (cluster.length > best.length) best = cluster;
    }
    if (best.length === 0) return;
    cores.push({ key, label: s.label, core: best });
    best.forEach((img) => coreAssigned.add(img.id));
  });

  // Adoption: any tile not in a core that sits inside a box's area joins it.
  const adopted = new Set<string>();
  const frames = cores.map(({ key, label, core }) => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const batchIds = new Set<string>();
    for (const img of core) {
      minX = Math.min(minX, img.x);
      minY = Math.min(minY, img.y);
      maxX = Math.max(maxX, img.x + img.displayWidth);
      maxY = Math.max(maxY, img.y + img.displayHeight);
      const b = getImageBatchId(img.src);
      if (b) batchIds.add(b);
    }
    const memberIds = core.map((i) => i.id);
    for (const img of images) {
      if (coreAssigned.has(img.id) || adopted.has(img.id) || img.workflowRunId != null) continue;
      // v4.25 — ANY overlap with the box area adopts (was center-point-in-box,
      // which made the box ignore a hovering tile until it was halfway in).
      if (
        img.x < maxX + FRAME_PAD &&
        img.x + img.displayWidth > minX - FRAME_PAD &&
        img.y < maxY + FRAME_PAD &&
        img.y + img.displayHeight > minY - FRAME_PAD_TOP
      ) {
        adopted.add(img.id);
        memberIds.push(img.id);
        minX = Math.min(minX, img.x);
        minY = Math.min(minY, img.y);
        maxX = Math.max(maxX, img.x + img.displayWidth);
        maxY = Math.max(maxY, img.y + img.displayHeight);
        const ab = getImageBatchId(img.src);
        if (ab) batchIds.add(ab);
      }
    }
    return { key, label, minX, minY, maxX, maxY, memberIds, batchIds };
  });

  // v4.45 — pending tiles count too: the section box appears the moment a
  // batch starts rendering instead of only after the ads land. A placeholder
  // overlapping an existing box stretches it; the rest cluster into their own
  // box labeled from the newest run ("Generating…" until the label loads).
  const claimed = new Set<PendingRect>();
  for (const f of frames) {
    for (const ph of pending) {
      if (claimed.has(ph)) continue;
      // v4.65 — claim by BATCH IDENTITY first: a frame adopts every loading
      // box of a batch whose images it contains, at any distance. Proximity
      // can't do this job — mixed-ratio grids leave 100-171px visual gaps
      // between narrow tiles and the next cell (cell pitch 340, tile widths
      // down to 169), while a neighboring run starts only 60px away, so no
      // fixed margin separates own-batch from next-run. The 48px proximity
      // test remains solely for legacy boxes with no batch info.
      const PENDING_CLAIM_MARGIN = 48;
      const batchMatched = (ph.batchId != null && f.batchIds.has(ph.batchId)) || (ph.workflowRunId != null && f.key === `run:${ph.workflowRunId}`);
      if (
        batchMatched ||
        (ph.batchId == null && ph.workflowRunId == null && ph.x < f.maxX + PENDING_CLAIM_MARGIN &&
          ph.x + ph.w > f.minX - PENDING_CLAIM_MARGIN &&
          ph.y < f.maxY + PENDING_CLAIM_MARGIN &&
          ph.y + ph.h > f.minY - PENDING_CLAIM_MARGIN)
      ) {
        claimed.add(ph);
        if (ph.taskId) f.memberIds.push(`ph-${ph.taskId}`);
        f.minX = Math.min(f.minX, ph.x);
        f.minY = Math.min(f.minY, ph.y);
        f.maxX = Math.max(f.maxX, ph.x + ph.w);
        f.maxY = Math.max(f.maxY, ph.y + ph.h);
      }
    }
  }
  const loose = pending.filter((p) => !claimed.has(p));
  // ≥2 loose tiles = a batch; a lone one-off generation doesn't get a box.
  if (loose.length > 0) {
    // v7.49 — BATCH IDENTITY first, proximity only as the fallback (the same
    // lesson as the v4.65 claim loop above). A 20-ad run's placeholders span
    // three rows 200px apart, so pure proximity drew a separate "Generating…"
    // box per row for ONE request. Rects that carry a batchId are one group
    // no matter how far apart they sit.
    const groups: PendingRect[][] = [];
    const byBatch = new Map<string, PendingRect[]>();
    const unbatched: PendingRect[] = [];
    for (const p of loose) {
      if (p.batchId) {
        const g = byBatch.get(p.batchId);
        if (g) g.push(p);
        else byBatch.set(p.batchId, [p]);
      } else {
        unbatched.push(p);
      }
    }
    byBatch.forEach((g) => groups.push(g));
    const unvisited = new Set<PendingRect>(unbatched);
    while (unvisited.size > 0) {
      const start = Array.from(unvisited)[0];
      unvisited.delete(start);
      const cluster = [start];
      const queue = [start];
      while (queue.length > 0) {
        const cur = queue.pop()!;
        Array.from(unvisited).forEach((other) => {
          if (
            cur.x - CLUSTER_GAP_X < other.x + other.w &&
            cur.x + cur.w + CLUSTER_GAP_X > other.x &&
            cur.y - CLUSTER_GAP_Y < other.y + other.h &&
            cur.y + cur.h + CLUSTER_GAP_Y > other.y
          ) {
            unvisited.delete(other);
            cluster.push(other);
            queue.push(other);
          }
        });
      }
      groups.push(cluster);
    }
    let pendingIdx = 0;
    for (const cluster of groups) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of cluster) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x + p.w);
        maxY = Math.max(maxY, p.y + p.h);
      }
      frames.push({
        key: `pending-${cluster[0].batchId ?? pendingIdx++}`,
        label: cluster.find(p => p.label)?.label ?? 'Generating…',
        minX,
        minY,
        maxX,
        maxY,
        memberIds: cluster.flatMap(p => p.taskId ? [`ph-${p.taskId}`] : []),
        batchIds: new Set(cluster.flatMap(p => p.batchId ? [p.batchId] : [])),
      });
    }
  }

  return frames.map(({ key, label, minX, minY, maxX, maxY, memberIds, batchIds }) => {
    const originX = minX - FRAME_PAD;
    const originY = minY - FRAME_PAD_TOP;
    return {
      id: `stage-frame-${key}`,
      type: 'funnelFrameNode',
      position: { x: originX, y: originY },
      data: {
        label,
        width: maxX - minX + FRAME_PAD * 2,
        height: maxY - minY + FRAME_PAD_TOP + FRAME_PAD,
        memberIds,
        batchIds: Array.from(batchIds),
        workflowRunIds: key.startsWith('run:') ? [Number(key.slice(4))] : [...new Set(pending.filter(p => p.taskId && memberIds.includes(`ph-${p.taskId}`)).flatMap(p => p.workflowRunId != null ? [p.workflowRunId] : []))],
        originX,
        originY,
      },
      // Pending-only boxes have no committed members to move — not draggable.
      draggable: memberIds.length > 0,
      dragHandle: '.frame-drag-handle',
      selectable: false,
      focusable: false,
      zIndex: -10,
    } as Node;
  });
}

function imageToNode(img: CanvasImage, selectedIds?: string[]): Node {
  const isSelected = selectedIds ? selectedIds.includes(img.id) : false;
  return {
    id: img.id,
    type: 'imageNode',
    position: { x: img.x, y: img.y },
    selected: isSelected,
    data: {
      src: img.src,
      prompt: img.prompt,
      displayWidth: img.displayWidth,
      displayHeight: img.displayHeight,
      isFavorite: img.isFavorite,
      videoUrl: img.videoUrl,
      selected: isSelected,
    },
    style: { width: img.displayWidth, height: img.displayHeight },
    dragHandle: undefined,
  };
}

function placeholderToNode(ph: CanvasPlaceholder): Node {
  return {
    id: `ph-${ph.taskId}`,
    type: 'placeholderNode',
    position: { x: ph.x, y: ph.y },
    data: {
      prompt: ph.prompt,
      statusText: ph.statusText,
      failed: ph.failed,
      errorMsg: ph.errorMsg,
      resolving: ph.resolving,
      displayWidth: ph.width,
      displayHeight: ph.height,
    },
    style: { width: ph.width, height: ph.height },
    // Retries can reuse an old failed run's positions. Keep that historical
    // failure behind real images, but show active generation above them.
    zIndex: ph.failed ? -1 : 1,
    draggable: false,
    selectable: false,
  };
}

const GUIDE_COLOR = '#6366F1';

function AlignmentGuideOverlay({ guides, viewport }: { guides: GuideLines; viewport: { x: number; y: number; zoom: number } }) {
  if (guides.horizontal.length === 0 && guides.vertical.length === 0) return null;

  const { x: vpX, y: vpY, zoom } = viewport;

  const toScreenX = (cx: number) => cx * zoom + vpX;
  const toScreenY = (cy: number) => cy * zoom + vpY;

  return (
    <Panel position="top-left" style={{ pointerEvents: 'none', position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}
      >
        {guides.horizontal.map((cy, i) => (
          <line
            key={`h-${i}`}
            x1={0}
            y1={toScreenY(cy)}
            x2="100%"
            y2={toScreenY(cy)}
            stroke={GUIDE_COLOR}
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        ))}
        {guides.vertical.map((cx, i) => (
          <line
            key={`v-${i}`}
            x1={toScreenX(cx)}
            y1={0}
            x2={toScreenX(cx)}
            y2="100%"
            stroke={GUIDE_COLOR}
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        ))}
      </svg>
    </Panel>
  );
}

function CanvasFlowInner() {
  const {
    clearPendingFitToView,
    clearPendingFitToRow,
    clearPendingPanTarget,
    updateImage,
    loadOrCreateProject,
    saveImagesSnapshot,
    undo,redo,undoStack,redoStack,
    activeTool,
    setActiveTool,
    selectedImageIds,
    setSelectedImageIds,
    commentMode,
    commentPopover,
    setCommentPopover,
    comments,
  } = useCanvasStore();
  const images = useActiveProjectImages();
  const selectionProjectId = useActiveProjectId();
  const messages = useActiveProjectMessages();
  const canvasPlaceholders = useActiveProjectCanvasPlaceholders();
  const pendingFitToView = useActiveProjectPendingFitToView();
  const pendingFitToRow = useActiveProjectPendingFitToRow();
  const pendingPanTarget = useActiveProjectPendingPanTarget();
  const projectLoading = useActiveProjectLoading();

  const { fitView, fitBounds, setCenter, getViewport, getNodes } = useReactFlow();

  const isHandMode = activeTool === 'hand';

  const initialNodes = useMemo((): Node[] => {
    const imgNodes = images.map((img) => imageToNode(img));
    const phNodes = Object.values(canvasPlaceholders).map(placeholderToNode);
    return [...imgNodes, ...phNodes];
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);

  const [guides, setGuides] = useState<GuideLines>({ horizontal: [], vertical: [] });
  const [viewport, setViewport] = useState(() => getViewport());

  const throttleRef = useRef<number | null>(null);
  const shiftHeldRef = useRef(false);

  // v4.19 — angleId → funnel stage map for section frames. Only fetched when
  // the project actually has stage-tagged tiles (adAngleId present).
  const storeFrameBrandId = useCanvasStore((s) =>
    s.activeBrandId ?? (s.activeProjectId != null ? s.projects[s.activeProjectId]?.brandId ?? null : null),
  );
  const frameProjectIdForBrand = useActiveProjectId();
  const hasAngleTiles = images.some((img) => img.adAngleId != null);
  // v4.26 — on a FRESH RELOAD the store has no brand id yet (activeBrandId is
  // lazily set at first chat send), which silently disabled the angles fetch
  // and made every FUNNEL box vanish ("half the boxes are missing"). Same
  // v3.87 fallback as the Preview panel: the server project row is ground truth.
  const { data: frameProjectRow } = useQuery<{ id: number; brandId?: number | null }>({
    queryKey: ['/api/canvas/projects', frameProjectIdForBrand],
    queryFn: async () => {
      const resp = await fetch(`/api/canvas/projects/${frameProjectIdForBrand}`);
      if (!resp.ok) return null as never;
      return resp.json();
    },
    staleTime: 60_000,
    enabled: frameProjectIdForBrand != null && storeFrameBrandId == null && hasAngleTiles,
  });
  const frameBrandId = storeFrameBrandId ?? frameProjectRow?.brandId ?? null;
  const { data: anglesResp } = useQuery<{ byStage: Record<string, Array<{ id: number }>> }>({
    queryKey: [`/api/brands/${frameBrandId}/ad-angles`],
    enabled: frameBrandId != null && hasAngleTiles,
    staleTime: 5 * 60_000,
  });
  const stageByAngleId = useMemo(() => {
    const map = new Map<number, string>();
    for (const [stage, arr] of Object.entries(anglesResp?.byStage ?? {})) {
      for (const a of arr) map.set(a.id, stage);
    }
    return map;
  }, [anglesResp]);

  // v4.21 — run → playbook label for non-funnel generation boxes. Fetched
  // only when the project has run-tagged tiles.
  const frameProjectId = useActiveProjectId();
  const hasRunTiles = images.some((img) => img.workflowRunId != null);
  // v4.45 — a generating batch (placeholders on canvas) also needs the runs
  // list, so the pending box can carry the playbook's label right away.
  const hasPendingTiles = Object.values(canvasPlaceholders).some(ph => !ph.failed);
  const hasFailedSlots=messages.some(message=>message.imageSlots?.some(slot=>slot.failed&&!slot.resolved));
  const { data: runsResp } = useQuery<FailedSlotRun[]>({
    queryKey: [`/api/workflows?projectId=${frameProjectId}`],
    enabled: frameProjectId != null && (hasRunTiles || hasPendingTiles || hasFailedSlots),
    staleTime: 60_000,
    // While generating, the run that owns this batch was just created — poll
    // so its label replaces the "Generating…" fallback within seconds.
    refetchInterval: hasPendingTiles ? 5_000 : false,
  });
  const visiblePlaceholders=useMemo(()=>failedSlotPlaceholders(Array.isArray(runsResp)?runsResp:[],messages,images,canvasPlaceholders),[runsResp,messages,images,canvasPlaceholders]);
  const cloneSources = useMemo(() => cloneFrameSources(images, Array.isArray(runsResp) ? runsResp : []), [images,runsResp]);
  const runLabelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const run of Array.isArray(runsResp) ? runsResp : []) {
      const label = PLAYBOOK_FRAME_LABELS[String(run.skeletonKey ?? '')];
      if (label) map.set(run.id, label);
    }
    return map;
  }, [runsResp]);
  const pendingRects = useMemo(
    () =>
      Object.values(visiblePlaceholders).map((ph) => ({
        taskId: ph.taskId,
        x: ph.x,
        y: ph.y,
        w: ph.width,
        h: ph.height,
        batchId: ph.batchId,
        label: ph.batchLabel ?? (ph.workflowRunId != null ? runLabelById.get(ph.workflowRunId) : undefined),
        workflowRunId: ph.workflowRunId,
      })),
    [visiblePlaceholders, runLabelById],
  );
  // v6.78 — only these runs keep TOF/MOF/BOF stage boxes.
  const fullFunnelRunIds = useMemo(() => {
    const s = new Set<number>();
    for (const run of Array.isArray(runsResp) ? runsResp : []) {
      if (run.skeletonKey === 'full_funnel_campaign') s.add(run.id);
    }
    return s;
  }, [runsResp]);

  // v4.24 — while a drag is active, the store→nodes rebuild must NOT run:
  // replacing nodes mid-drag resets the dragged node's position (observed:
  // title-chip group drag "moved only the box" because the rebuild clobbered
  // it). Live updates during drag happen directly in handleNodeDrag.
  const draggingRef = useRef(false);
  const frameDragOrigin = useRef<{x:number;y:number; existingIds:Set<string>; memberSlots:Array<{x:number;y:number;taskId:string;batchId?:string}>}|null>(null);

  // Sync store images → RF nodes (frames render behind, then images, then placeholders)
  useEffect(() => {
    if (draggingRef.current) return;
    const frameNodes = buildStageFrameNodes(
      images,
      stageByAngleId,
      runLabelById,
      pendingRects,
      fullFunnelRunIds,
      cloneSources,
    );
    const imgNodes = images.map((img) => imageToNode(img, selectedImageIds));
    const phNodes = Object.values(visiblePlaceholders).map(placeholderToNode);
    setNodes([...frameNodes, ...imgNodes, ...phNodes]);
  }, [images, visiblePlaceholders, selectedImageIds, setNodes, stageByAngleId, runLabelById, pendingRects, fullFunnelRunIds, cloneSources]);

  // On mount: load project
  useEffect(() => {
    loadOrCreateProject();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // v5.13 — video-clone tile↔scene linkage survives reloads: whenever a
  // project lands on this canvas (initial load AND switchProject both settle
  // by setting activeProjectId), rebuild the runner's src→scene map from the
  // drafts-list endpoint. Fire-and-forget; non-admins 401/403 inside and the
  // map simply stays empty (no hover buttons).
  useEffect(() => {
    if (frameProjectId == null) return;
    void rebuildVideoCloneTileIndex(frameProjectId);
  }, [frameProjectId]);

  // v6.19 — self-heal RAW recovery rows on load. A video row parked at the
  // persist-first insert signature ((0,0) 720×1280 raw) means its producer
  // never posted the real layout — a dropped SSE mid-run, or an EXTERNAL
  // canvas writer (edit.admoji.com inserted `vedit-*` rows): it renders as
  // a GIANT tile covering the project ("the video is HUGE, the source is
  // gone"). Re-place it below the current content at standard tile size.
  // Mid-run rows are safe to touch: the real merge overwrites layout by
  // (project_id, src) afterward anyway.
  const healedOrphansRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (frameProjectId == null || images.length < 2) return;
    const isRaw = (im: { x: number; y: number; displayWidth: number; displayHeight: number }) =>
      im.x === 0 && im.y === 0 && im.displayWidth === 720 && im.displayHeight === 1280;
    const orphans = images.filter((im) => im.videoUrl && isRaw(im) && !healedOrphansRef.current.has(im.id));
    if (orphans.length === 0) return;
    const others = images.filter((im) => !isRaw(im));
    if (others.length === 0) return;
    const { updateImage } = useCanvasStore.getState();
    let yCursor = Math.max(...others.map((im) => im.y + im.displayHeight));
    for (const im of orphans) {
      healedOrphansRef.current.add(im.id);
      yCursor += 48;
      updateImage(im.id, { x: 60, y: yCursor, displayWidth: 169, displayHeight: 300 });
      yCursor += 300;
    }
  }, [frameProjectId, images.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // pendingFitToView → fitView() after 300ms (panel CSS transition is 300ms)
  useEffect(() => {
    if (!pendingFitToView) return;
    if (images.length > 0) { clearPendingFitToView(); return; }
    const timer = setTimeout(() => {
      fitView({ padding: 0.1, duration: 400 });
      clearPendingFitToView();
    }, 300);
    return () => clearTimeout(timer);
  }, [pendingFitToView, fitView, clearPendingFitToView, images.length]);

  // v5.07 — pendingFitToRow finally has a MOUNTED consumer. The generation
  // path has always written it (placeholder placement, every image landing,
  // and now the playbook_complete finalize) so the camera follows the batch —
  // but its only handler lived in the legacy Konva workspace, which nothing
  // mounts. Any layout drift mid-run therefore left the user staring at empty
  // grid until a hard refresh (run 1020: batch walked to x=-4020, viewport
  // never followed).
  useEffect(() => {
    if (!pendingFitToRow) return;
    if (images.length > 0) { clearPendingFitToRow(); return; }
    const { minX, minY, maxX, maxY } = pendingFitToRow;
    if (![minX, minY, maxX, maxY].every(Number.isFinite) || maxX <= minX || maxY <= minY) {
      clearPendingFitToRow();
      return;
    }
    const timer = setTimeout(() => {
      fitBounds(
        { x: minX - 40, y: minY - 40, width: maxX - minX + 80, height: maxY - minY + 80 },
        { duration: 400 },
      );
      clearPendingFitToRow();
    }, 300);
    return () => clearTimeout(timer);
  }, [pendingFitToRow, fitBounds, clearPendingFitToRow, images.length]);

  // pendingPanTarget → setCenter() after 300ms (wait for panel CSS transition)
  useEffect(() => {
    if (!pendingPanTarget) return;
    const timer = setTimeout(() => {
      setCenter(pendingPanTarget.x, pendingPanTarget.y, { zoom: 0.85, duration: 300 });
      clearPendingPanTarget();
    }, 300);
    return () => clearTimeout(timer);
  }, [pendingPanTarget, setCenter, clearPendingPanTarget]);

  // Auto-save local edits after 3s; a freshly hydrated view must remain read-only.
  // Capture the active project at the time the effect runs so the save lands
  // in this project's row even if the user clicks away during the 3s window.
  const activeProjectIdForSave = useActiveProjectId();
  const imageSaveStatus = useCanvasStore(s=>activeProjectIdForSave==null?undefined:s.imageSaveStatusByProject[activeProjectIdForSave]);
  useEffect(() => {
    if (activeProjectIdForSave == null) return;
    const projectIdAtSchedule = activeProjectIdForSave;
    const timer = setTimeout(() => {
      saveImagesSnapshot(projectIdAtSchedule,{onlyLocalEdits:true});
    }, 3000);
    return () => clearTimeout(timer);
  }, [images, activeProjectIdForSave]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearGuides = useCallback(() => {
    setGuides({ horizontal: [], vertical: [] });
    shiftHeldRef.current = false;
    if (throttleRef.current !== null) {
      clearTimeout(throttleRef.current);
      throttleRef.current = null;
    }
  }, []);

  // A shared client ID can reuse its DOM node across chats. Remember this
  // exact click's origin before a descendant capture listener can navigate;
  // the delegated node handler may already have the destination's props.
  const nodeClickProjects = useRef(new WeakMap<Event, number | null>());
  const handleNodeClick: NodeMouseHandler = useCallback(
    (event, node) => {
      if (nodeClickProjects.current.get(event.nativeEvent) !== selectionProjectId || useCanvasStore.getState().activeProjectId !== selectionProjectId) return;
      if (node.type !== 'imageNode') return;
      // Hand mode still SELECTS on a plain click (v4.98 — "when I use the
      // Hand tool it doesn't work to select it as reference"). Pan safety:
      // React Flow only fires onNodeClick when the pointer stayed within the
      // drag threshold, so pans that start on a tile never select.

      // Comment-mode hijack: click on image → open the comment popover
      // pinned where the user clicked. Does NOT change canvas selection —
      // selecting the commented image would (a) add it as a chat-reference
      // image so only the last-commented one would flow through, and
      // (b) trigger React Flow's selection-change auto-pan which jumped
      // the view.
      if (commentMode && !event.shiftKey) {
        const img = images.find((i) => i.id === node.id);
        if (img) {
          const targetEl = event.currentTarget as HTMLElement | null;
          const rect = targetEl?.getBoundingClientRect();
          const localX = rect ? Math.max(0, event.clientX - rect.left) : img.displayWidth / 2;
          const localY = rect ? Math.max(0, event.clientY - rect.top) : img.displayHeight / 2;
          // Scale local rendered px back to image-display pixels (RF
          // CSS-transforms nodes; rect.width is scaled).
          const scaleX = rect && rect.width > 0 ? img.displayWidth / rect.width : 1;
          const scaleY = rect && rect.height > 0 ? img.displayHeight / rect.height : 1;
          const pinX = Math.min(img.displayWidth, localX * scaleX);
          const pinY = Math.min(img.displayHeight, localY * scaleY);
          setCommentPopover({
            imageId: node.id,
            pinX,
            pinY,
            screenX: event.clientX,
            screenY: event.clientY,
          });
          return;
        }
      }

      if (event.shiftKey) {
        setSelectedImageIds(
          selectedImageIds.includes(node.id)
            ? selectedImageIds.filter((id) => id !== node.id)
            : [...selectedImageIds, node.id],
          selectionProjectId
        );
      } else {
        setSelectedImageIds([node.id], selectionProjectId);
      }
    },
    [selectedImageIds, setSelectedImageIds, commentMode, images, setCommentPopover, selectionProjectId]
  );

  const handlePaneClick = useCallback(() => {
    if (isHandMode) return;
    if (selectedImageIds.length > 0) {
      setSelectedImageIds([], selectionProjectId);
    }
  }, [selectedImageIds, setSelectedImageIds, isHandMode, selectionProjectId]);

  // Sync rubber-band drag selection → store once, when the drag ends.
  // Reading ReactFlow's committed node state via getNodes() avoids the
  // race cascade that occurred when syncing on every onSelectionChange fire.
  const handleSelectionEnd = useCallback(() => {
    const ids = getNodes()
      .filter((n) => n.selected && n.type === 'imageNode')
      .map((n) => n.id);
    setSelectedImageIds(ids, selectionProjectId);
  }, [getNodes, setSelectedImageIds, selectionProjectId]);

  const lastFramePosRef = useRef<{ x: number; y: number } | null>(null);
  const frameThrottleRef = useRef<number | null>(null);

  const imageDragOrigin=useRef<{projectId:number;positions:Map<string,{x:number;y:number}>}|null>(null);
  // React Flow emits keyboard positions synchronously during its native keydown
  // handler, without the pointer drag callbacks. Keep that one event's ownership
  // explicit so selection, resize and later asynchronous updates cannot become moves.
  const keyboardMoveContext=useRef<{projectId:number;imageIds:Set<string>;event:KeyboardEvent}|null>(null);
  const captureKeyboardMove=useCallback((event:ReactKeyboardEvent<HTMLDivElement>)=>{
    keyboardMoveContext.current=null;
    if(event.defaultPrevented||event.nativeEvent.isComposing||!['ArrowRight','ArrowLeft','ArrowUp','ArrowDown'].includes(event.key))return;
    const target=event.target as HTMLElement;
    if(target.closest('input,textarea,select,button,[contenteditable="true"],[role="textbox"]'))return;
    if(!target.closest('.react-flow__node-imageNode,.react-flow__nodesselection-rect'))return;
    const store=useCanvasStore.getState();const projectId=store.activeProjectId;
    if(projectId==null)return;
    const ownedIds=new Set((store.projects[projectId]?.images??[]).map(image=>image.id));
    const imageIds=new Set(getNodes().filter(node=>node.type==='imageNode'&&node.selected&&node.draggable!==false&&ownedIds.has(node.id)).map(node=>node.id));
    if(!imageIds.size)return;
    const context={projectId,imageIds,event:event.nativeEvent};keyboardMoveContext.current=context;
    // Capture and bubble use separate native listeners: a microtask from capture
    // can run before React Flow's bubble handler. Bubble clears normally; the
    // task-end fallback also clears when a descendant stops propagation.
    setTimeout(()=>{if(keyboardMoveContext.current===context)keyboardMoveContext.current=null;},0);
  },[getNodes]);
  const handleNodesChange=useCallback<OnNodesChange>((changes)=>{
    const context=keyboardMoveContext.current;
    if(!context||!context.event.defaultPrevented||![Event.AT_TARGET,Event.BUBBLING_PHASE].includes(context.event.eventPhase)){onNodesChange(changes);return;}
    // Consume only the matching synchronous keyboard position batch. React Flow
    // has already prevented default by here; that is its normal movement path.
    const positions=changes.filter(change=>change.type==='position'&&change.position&&context.imageIds.has(change.id));
    if(!positions.length){onNodesChange(changes);return;}
    keyboardMoveContext.current=null;
    const store=useCanvasStore.getState();
    if(store.activeProjectId!==context.projectId){
      onNodesChange(changes.filter(change=>change.type!=='position'||!context.imageIds.has(change.id)));
      // The native handler can already have mutated its internal lookup. Reset
      // only these stale IDs from the newly active project's canonical images.
      const currentImages=store.activeProjectId==null?[]:store.projects[store.activeProjectId]?.images??[];
      setNodes(current=>current.map(node=>{
        const image=context.imageIds.has(node.id)?currentImages.find(image=>image.id===node.id):undefined;
        return image?{...node,position:{x:image.x,y:image.y}}:node;
      }));
      return;
    }
    onNodesChange(changes);
    const images=store.projects[context.projectId]?.images??[];
    const moves=positions.flatMap(change=>{
      if(change.type!=='position'||!change.position)return [];
      const image=images.find(image=>image.id===change.id);
      return image?[{id:image.id,fromX:image.x,fromY:image.y,toX:change.position.x,toY:change.position.y}]:[];
    });
    if(moves.length)store.commitMove(moves);
  },[onNodesChange,setNodes]);
  useEffect(()=>{
    const key=(event:KeyboardEvent)=>{
      const target=event.target as HTMLElement|null;
      if(event.defaultPrevented||event.isComposing||target?.closest('input,textarea,[contenteditable="true"],[role="textbox"]')||!(event.metaKey||event.ctrlKey))return;
      const letter=event.key.toLowerCase();const redo=(letter==='z'&&event.shiftKey)||letter==='y';
      if(letter!=='z'&&letter!=='y')return;
      const store=useCanvasStore.getState();if(!(redo?store.redoStack:store.undoStack).length)return;
      event.preventDefault();redo?store.redo():store.undo();
    };
    window.addEventListener('keydown',key);return()=>window.removeEventListener('keydown',key);
  },[]);

  const handleNodeDragStart: OnNodeDrag = useCallback(
    (event, node, nodes) => {
      draggingRef.current = true;
      if (node.type === 'funnelFrameNode') {
        lastFramePosRef.current = { x: node.position.x, y: node.position.y };
        const store = useCanvasStore.getState();
        const project = store.activeProjectId != null ? store.projects[store.activeProjectId] : undefined;
        frameDragOrigin.current = { ...node.position, existingIds: new Set([
          ...(project?.images.map(image => image.id) ?? []),
          ...Object.keys(visiblePlaceholders).map(id => `ph-${id}`),
        ]), memberSlots: Object.values(visiblePlaceholders).filter(ph => ((node.data as {memberIds?:string[]}).memberIds ?? []).includes(`ph-${ph.taskId}`)).map(ph => ({x:ph.x,y:ph.y,taskId:ph.taskId,batchId:ph.batchId})) };
        return;
      }
      if (node.type !== 'imageNode') return;
      const store=useCanvasStore.getState();const projectId=store.activeProjectId;
      if(projectId!=null){const moving=new Set((nodes?.length?nodes:[node]).map(row=>row.id));imageDragOrigin.current={projectId,positions:new Map((store.projects[projectId]?.images??[]).filter(image=>moving.has(image.id)).map(image=>[image.id,{x:image.x,y:image.y}]))};}
      shiftHeldRef.current = event.shiftKey;
    },
    [visiblePlaceholders]
  );

  const handleNodeDrag: OnNodeDrag = useCallback(
    (event, node) => {
      // v4.24 — title-chip group drag: move member ads WITH the box, live
      // ("locked in place"), by applying each tick's delta to member nodes.
      if (node.type === 'funnelFrameNode') {
        const last = lastFramePosRef.current;
        if (!last) return;
        const dx = node.position.x - last.x;
        const dy = node.position.y - last.y;
        if (dx === 0 && dy === 0) return;
        lastFramePosRef.current = { x: node.position.x, y: node.position.y };
        const memberIds = new Set(((node.data as { memberIds?: string[] }).memberIds) ?? []);
        setNodes((currentNodes) =>
          currentNodes.map((n) =>
            memberIds.has(n.id)
              ? { ...n, position: { x: n.position.x + dx, y: n.position.y + dy } }
              : n,
          ),
        );
        return;
      }
      if (node.type !== 'imageNode') return;
      shiftHeldRef.current = event.shiftKey;

      // v4.24 — live box preview: while a tile is dragged, re-derive the
      // frames with the tile at its CURRENT position so the target box
      // visibly expands to adopt it on hover (and the old box releases it).
      if (frameThrottleRef.current === null) {
        frameThrottleRef.current = window.setTimeout(() => {
          frameThrottleRef.current = null;
          setNodes((currentNodes) => {
            const liveImages = images.map((img) => {
              const n = currentNodes.find((cn) => cn.id === img.id && cn.type === 'imageNode');
              return n ? { ...img, x: n.position.x, y: n.position.y } : img;
            });
            const frameNodes = buildStageFrameNodes(
              liveImages,
              stageByAngleId,
              runLabelById,
              pendingRects,
              fullFunnelRunIds,
      cloneSources,
            );
            return [...frameNodes, ...currentNodes.filter((n) => n.type !== 'funnelFrameNode')];
          });
        }, 60);
      }

      if (!event.shiftKey) {
        setGuides({ horizontal: [], vertical: [] });
        return;
      }

      if (throttleRef.current !== null) return;

      throttleRef.current = window.setTimeout(() => {
        throttleRef.current = null;

        setNodes((currentNodes) => {
          const result = computeSnapAndGuides(node, currentNodes);

          if (result.dx === 0 && result.dy === 0) {
            setGuides(result.guides);
            return currentNodes;
          }

          setGuides(result.guides);
          setViewport(getViewport());

          return currentNodes.map((n) => {
            if (n.id !== node.id) return n;
            return {
              ...n,
              position: {
                x: node.position.x + result.dx,
                y: node.position.y + result.dy,
              },
            };
          });
        });
      }, 16);
    },
    [setNodes, getViewport, images, stageByAngleId, runLabelById, pendingRects, fullFunnelRunIds]
  );

  const handleNodeDragStop: OnNodeDrag = useCallback(
    (_event, node) => {
      clearGuides();
      draggingRef.current = false;
      // v4.24 — group drag commit: members followed the box live during the
      // drag (handleNodeDrag), so persist their CURRENT react-flow positions.
      // No origin-delta math — immune to mid-drag rebuild/position drift.
      if (node.type === 'funnelFrameNode') {
        lastFramePosRef.current = null;
        const memberIds = new Set(((node.data as { memberIds?: string[] }).memberIds) ?? []);
        const origin = frameDragOrigin.current;
        frameDragOrigin.current = null;
        if (!origin) return;
        const dx = node.position.x - origin.x, dy = node.position.y - origin.y;
        const store = useCanvasStore.getState();
        const projectId = store.activeProjectId;
        const belongs = (item:{id?:string;taskId?:string;x:number;y:number;src?:string;batchId?:string;workflowRunId?:number|null}) =>
          memberIds.has(item.taskId ? `ph-${item.taskId}` : item.id ?? '') ||
          (!origin.existingIds.has(item.taskId ? `ph-${item.taskId}` : item.id ?? '') && (
            origin.memberSlots.some(slot => slot.taskId === `agent-img-${item.id}` || (Math.abs(slot.x-item.x)<1 && Math.abs(slot.y-item.y)<1))
          ));
        // Read current store members: an in-flight slot may have become an image
        // while the React Flow node list was frozen for this drag.
        store.commitMove((projectId != null ? store.projects[projectId]?.images ?? [] : []).filter(belongs).map(img => ({id:img.id,fromX:img.x,fromY:img.y,toX:img.x+dx,toY:img.y+dy})));
        store._writeCanvasPlaceholders(projectId, current => Object.fromEntries(
          Object.entries({...Object.fromEntries(Object.entries(visiblePlaceholders).filter(([id,ph]) => ph.failed && !canvasPlaceholders[id])),...current}).map(([id,ph]) => [id, belongs(ph) ? {...ph,x:ph.x+dx,y:ph.y+dy} : ph])
        ));
        if (projectId != null) {
          const moved = Object.values(useCanvasStore.getState().projects[projectId].canvasPlaceholders).filter(belongs);
          store._writeMessages(projectId, messages => messages.map(message => {
            const own = moved.filter(ph => ph.batchId === message.id || (ph.workflowRunId != null && message.playbookProgress?.workflowRunId === ph.workflowRunId));
            const originalSlots = origin.memberSlots.filter(slot => slot.batchId === message.id);
            if (!own.length && !originalSlots.length) return message;
            const positions = {...message.canvasSlotPositions};
            for (const slot of originalSlots) positions[`request:${slot.taskId.replace(/^agent-img-/, "")}`] = {x:slot.x+dx,y:slot.y+dy};
            for (const ph of own) if (ph.workflowRunId != null && ph.slotIndex != null) positions[`${ph.workflowRunId}:${ph.slotIndex}`] = {x:ph.x,y:ph.y};
            return {...message,canvasSlotPositions:positions};
          }));
          void store.saveConversationHistory(projectId);
        }
        return;
      }
      if (node.type !== 'imageNode') return;
      // v4.42 — shift-snap LOCKS on release: mid-drag position nudges get
      // overwritten by the next pointer event, so guides showed but nothing
      // aligned. On drop with Shift held, snap to the nearest other tile's
      // left edge (x) and top (y) within 48px — makes clean rows/columns.
      let fx = node.position.x;
      let fy = node.position.y;
      if (shiftHeldRef.current) {
        const TH = 48;
        let bestDx = Number.POSITIVE_INFINITY;
        let bestDy = Number.POSITIVE_INFINITY;
        for (const n of getNodes()) {
          if (n.id === node.id || n.type !== 'imageNode') continue;
          const dx = n.position.x - fx;
          const dy = n.position.y - fy;
          if (Math.abs(dx) < Math.abs(bestDx)) bestDx = dx;
          if (Math.abs(dy) < Math.abs(bestDy)) bestDy = dy;
        }
        if (Math.abs(bestDx) <= TH) fx += bestDx;
        if (Math.abs(bestDy) <= TH) fy += bestDy;
        if (fx !== node.position.x || fy !== node.position.y) {
          setNodes((cur) => cur.map((n) => (n.id === node.id ? { ...n, position: { x: fx, y: fy } } : n)));
        }
      }
      shiftHeldRef.current = false;
      const origin=imageDragOrigin.current;imageDragOrigin.current=null;const store=useCanvasStore.getState();
      if(origin&&origin.projectId===store.activeProjectId){
        const changes=getNodes().filter(row=>origin.positions.has(row.id)).map(row=>{const before=origin.positions.get(row.id)!;return{id:row.id,fromX:before.x,fromY:before.y,toX:row.id===node.id?fx:row.position.x,toY:row.id===node.id?fy:row.position.y};}).filter(change=>change.fromX!==change.toX||change.fromY!==change.toY);
        if(changes.length)store.commitMove(changes);
      }
    },
    [updateImage, clearGuides, getNodes, setNodes, visiblePlaceholders, canvasPlaceholders]
  );

  const handleMove = useCallback(() => {
    setViewport(getViewport());
  }, [getViewport]);

  const hasGuides = guides.horizontal.length > 0 || guides.vertical.length > 0;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} onKeyDownCapture={captureKeyboardMove} onKeyDown={()=>{keyboardMoveContext.current=null;}}
      onClickCapture={event=>nodeClickProjects.current.set(event.nativeEvent,selectionProjectId)}>
      {imageSaveStatus && <div data-testid="canvas-save-status" role="status" aria-live="polite"
        style={{position:'absolute',top:12,right:52,zIndex:20,pointerEvents:'none',padding:'7px 10px',borderRadius:8,background:'#FFFFFF',color:imageSaveStatus==='error'?'#B91C1C':'#526074',fontSize:12,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}>
        {imageSaveStatus==='saving'?'Saving Canvas…':imageSaveStatus==='saved'?'Canvas saved':'Canvas changes not saved'}
      </div>}
      {/* Pointer / Hand toggle buttons — top-left of canvas */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 20,
          display: 'flex',
          gap: 4,
        }}
      >
        <button
          data-testid="canvas-mode-pointer"
          title="Pointer / Select"
          onClick={() => setActiveTool('select')}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `1.5px solid ${!isHandMode ? '#6366F1' : '#DCE3ED'}`,
            background: !isHandMode ? 'rgba(99,102,241,0.08)' : '#FFFFFF',
            color: !isHandMode ? '#6366F1' : '#1A1A1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <MousePointer2 size={15} />
        </button>
        <button
          data-testid="canvas-mode-hand"
          title="Hand / Pan"
          onClick={() => setActiveTool('hand')}
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `1.5px solid ${isHandMode ? '#6366F1' : '#DCE3ED'}`,
            background: isHandMode ? 'rgba(99,102,241,0.08)' : '#FFFFFF',
            color: isHandMode ? '#6366F1' : '#1A1A1A',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <Hand size={15} />
        </button>
        {([{label:'Undo',icon:Undo2,disabled:!undoStack.length,action:undo,shortcut:'⌘Z / Ctrl+Z',keys:'Meta+Z Control+Z'},{label:'Redo',icon:Redo2,disabled:!redoStack.length,action:redo,shortcut:'⌘⇧Z / Ctrl+Shift+Z',keys:'Meta+Shift+Z Control+Shift+Z Control+Y'}]).map(({label,icon:Icon,disabled,action,shortcut,keys})=><button key={label} data-testid={`canvas-${label.toLowerCase()}`} aria-label={label} aria-keyshortcuts={keys} title={`${label} (${shortcut})`} disabled={disabled} onClick={action}
          style={{width:32,height:32,borderRadius:8,border:'1.5px solid #DCE3ED',background:'#FFFFFF',color:disabled?'#9CA3AF':'#1A1A1A',display:'flex',alignItems:'center',justifyContent:'center',cursor:disabled?'default':'pointer',opacity:disabled?0.5:1,boxShadow:'0 1px 4px rgba(0,0,0,0.08)'}}><Icon size={15}/></button>)}
      </div>

      {/* v6.53 — caption preset picker (fixed overlay; must sit outside the
          transformed React Flow pane) */}
      <CaptionStylePicker />

      <ReactFlow
        nodes={nodes}
        edges={[]}
        nodeTypes={NODE_TYPES}
        onNodesChange={handleNodesChange}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        // Capture the drag origin on press. XYFlow's positive threshold starts
        // from the first move event, dropping its delta; a coalesced one-move
        // drag then ends without moving at all. Clicks still commit no history.
        nodeDragThreshold={0}
        onNodeDragStart={handleNodeDragStart}
        onNodeDrag={handleNodeDrag}
        onNodeDragStop={handleNodeDragStop}
        onMove={handleMove}
        minZoom={0.05}
        maxZoom={4}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
        panOnDrag={isHandMode}
        panOnScroll={!isHandMode}
        panOnScrollMode={PanOnScrollMode.Free}
        selectionOnDrag={!isHandMode}
        multiSelectionKeyCode="Shift"
        onSelectionEnd={handleSelectionEnd}
      >
        <Background color="#e4e9f1" gap={24} size={1.3} />
        <NewTaskDotBackground grid={{
          gap: 24 * viewport.zoom,
          // Match React Flow's dot center and default pattern translation.
          offsetX: viewport.x - 1 - 12 * viewport.zoom + 0.65 * viewport.zoom,
          offsetY: viewport.y - 1 - 12 * viewport.zoom + 0.65 * viewport.zoom,
          radius: 0.65 * viewport.zoom,
        }} />
        <Controls position="bottom-right" />
        {hasGuides && <AlignmentGuideOverlay guides={guides} viewport={viewport} />}

        {/* Comment pin overlay — small numbered markers on every image with
            pending comments. Positioned in flow-canvas coords (image.x +
            comment.pinX), so they pan/zoom with the canvas. */}
        {comments.filter((c) => c.isPending).length > 0 && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
              transformOrigin: '0 0',
              zIndex: 9,
            }}
          >
            {images.flatMap((img) => {
              const imgComments = comments.filter(
                (c) => c.imageId === img.id && c.isPending,
              );
              return imgComments.map((c, idx) => (
                <div
                  key={c.id}
                  title={c.text}
                  style={{
                    position: 'absolute',
                    left: img.x + c.pinX - 12,
                    top: img.y + c.pinY - 12,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#6366f1',
                    color: 'white',
                    border: '2px solid white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Re-open the popover for this image so the user can
                    // edit or delete this comment / add another.
                    setCommentPopover({
                      imageId: img.id,
                      pinX: c.pinX,
                      pinY: c.pinY,
                      screenX: e.clientX,
                      screenY: e.clientY,
                    });
                  }}
                >
                  {idx + 1}
                </div>
              ));
            })}
          </div>
        )}
      </ReactFlow>

      {/* Comment popover — same component the old Konva canvas used. Self-
          contained: reads commentPopover state from the store. */}
      {commentPopover && <CanvasCommentPin popover={commentPopover} />}

      {activeTool === 'brush' && (
        <CanvasBrushMode onClose={() => setActiveTool('select')} />
      )}
    </div>
  );
}

export function CanvasFlowWorkspace() {
  return (
    <ReactFlowProvider>
      <div style={{ width: '100%', height: '100%' }}>
        <CanvasFlowInner />
      </div>
    </ReactFlowProvider>
  );
}
